import { useQuery, useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import api from '../api/axios';

/* ─── tipos ─── */
export interface DiaEspecial {
  id: number;
  idProfesor: number;
  tramoId?: string | null;
  idCentro?: number | null;
  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:MM o HH:MM:SS
  horaFin: string; // HH:MM o HH:MM:SS
  /** 1=presencial, 2=online, 4=a_domicilio (combinables: 3,5,6,7) */
  formatosMask: number;
  comentario?: string | null;
  /** si la fila es un bloqueo total/parcial (en backend está soportado) */
  bloqueo?: boolean;
}

/** DTO para crear especial (POST) */
export type FormatoStr = 'presencial' | 'online' | 'a_domicilio';

export interface CreateEspecialPayload {
  idProfesor: number;
  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:MM
  horaFin: string; // HH:MM
  formatos?: FormatoStr[]; // si no se informa, backend usa formatos asignados del profesor (o 7 si no tiene)
  /** una de estas dos variantes (o ninguna si es solo online) */
  idCentro?: number;
  centros?: number[];
  comentario?: string;
  /** si true → bloqueo real del día/tramo */
  bloqueo?: boolean;
}

/* ─── helpers de mapeo para UPDATE ─── */
const BIT_P = 1,
  BIT_O = 2,
  BIT_D = 4;
const maskToArray = (mask: number): FormatoStr[] => {
  const out: FormatoStr[] = [];
  if (mask & BIT_P) out.push('presencial');
  if (mask & BIT_O) out.push('online');
  if (mask & BIT_D) out.push('a_domicilio');
  return out; // sin fallback
};
const hhmm = (s?: string) => (s ? String(s).slice(0, 5) : s);

/** Partial payload que acepta el backend en PUT */
type UpdateEspecialPayload = Partial<{
  horaInicio: string; // HH:MM
  horaFin: string; // HH:MM
  bloqueo: boolean;
  idCentro: number | null; // backend acepta null
  formatos: FormatoStr[]; // [] ⇒ usar asignados del profesor (back lo soporta)
  comentario: string | null;
}>;

const buildUpdatePayload = (dto: DiaEspecial): UpdateEspecialPayload => {
  const p: UpdateEspecialPayload = {};
  // ❌ No enviar fecha: el back no la actualiza
  if (dto.horaInicio) p.horaInicio = hhmm(dto.horaInicio)!;
  if (dto.horaFin) p.horaFin = hhmm(dto.horaFin)!;
  if (typeof dto.bloqueo === 'boolean') p.bloqueo = dto.bloqueo;
  // Solo incluir idCentro si viene definido (evita forzar null por defecto)
  if (dto.idCentro !== undefined) p.idCentro = dto.idCentro ?? null;
  if (Number.isFinite(dto.formatosMask)) p.formatos = maskToArray(Number(dto.formatosMask));
  if (dto.comentario !== undefined) p.comentario = dto.comentario ?? null;
  return p;
};

/* ─── hook ─── */
export const useDiasEspeciales = (profesorId?: number, desde?: string, hasta?: string) => {
  const qc = useQueryClient();
  const key: QueryKey = ['dias-especiales', profesorId ?? 0, desde ?? '', hasta ?? ''];

  /* GET */
  const {
    data = [],
    isPending: isLoading,
    error,
  } = useQuery<DiaEspecial[]>({
    queryKey: key,
    enabled: Boolean(profesorId && desde && hasta),
    queryFn: async () => {
      const { data } = await api.get<DiaEspecial[]>('/disponibilidad-especial', {
        params: { profesor: profesorId, desde, hasta },
      });
      return data;
    },
  });

  /* helper */
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  /* POST */
  const create = useMutation({
    mutationFn: (dto: CreateEspecialPayload) => api.post('/disponibilidad-especial', dto),
    onSuccess: invalidate,
  });

  /* PUT (mapea formatosMask → formatos[], y recorta HH:MM) */
  const update = useMutation({
    mutationFn: (dto: DiaEspecial) =>
      api.put(`/disponibilidad-especial/${dto.id}`, buildUpdatePayload(dto)),
    onSuccess: invalidate,
  });

  /* DELETE */
  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/disponibilidad-especial/${id}`),
    onSuccess: invalidate,
  });

  return {
    data,
    isLoading,
    error,
    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
  };
};
